// src/features/dashboard/pages/Employee/EmployeeActiveHoursReport.tsx

import {
  useEffect,
  useRef,
} from 'react';

import {
  useLoaderContext,
} from '../../../../components/Loader/LoaderContext';

import DashboardHeader from '../../components/DashboardHeader';

import ActiveHoursHeader from '../../components/EmployeeActiveHoursReport/ActiveHoursHeader';

import ActiveHoursKpiCards from '../../components/EmployeeActiveHoursReport/ActiveHoursKpiCards';

import HourlyActiveMinutesChart from '../../components/EmployeeActiveHoursReport/HourlyActiveMinutesChart';

import TimeUtilisationDonut from '../../components/EmployeeActiveHoursReport/TimeUtilisationDonut';

import WTDActiveHoursTrend from '../../components/EmployeeActiveHoursReport/WTDActiveHoursTrend';

import MeVsTeamActiveHours from '../../components/EmployeeActiveHoursReport/MeVsTeamActiveHours';

import ActiveHoursHistory30Days from '../../components/EmployeeActiveHoursReport/ActiveHoursHistory30Days';

import WeeklyActiveHoursDetail from '../../components/EmployeeActiveHoursReport/WeeklyActiveHoursDetail';

import {
  useEmployeeActiveHours,
} from '../../hooks/Employee/useEmployeeActiveHours';


/* ============================================================================
 * BROWSER ZOOM AUTO REFRESH
 *
 * Chrome browser zoom:
 *
 *     80%
 *      ↓
 *     100%
 *      ↓
 *     125%
 *
 * changes window.devicePixelRatio.
 *
 * We continuously check devicePixelRatio so that the page automatically
 * refreshes immediately after browser zoom changes.
 *
 * IMPORTANT:
 *
 * - Normal component rendering is NOT changed.
 * - API code is NOT changed.
 * - Date filter is NOT changed.
 * - Charts are NOT changed.
 * - Only browser zoom detection is added.
 * ========================================================================== */

const useBrowserZoomRefresh = () => {

  const initialDevicePixelRatio =
    useRef<number>(
      window.devicePixelRatio,
    );

  const reloadTriggered =
    useRef<boolean>(
      false,
    );


  useEffect(() => {

    /*
     * Store the device pixel ratio that existed when the page loaded.
     *
     * Example:
     *
     * Page loaded at 80%
     * devicePixelRatio = 0.8
     *
     * Later user changes to 125%
     * devicePixelRatio = 1.25
     */

    initialDevicePixelRatio.current =
      window.devicePixelRatio;


    /* ========================================================================
     * CHECK CURRENT ZOOM
     * ====================================================================== */

    const checkBrowserZoom =
      () => {

        /*
         * Do not check again after refresh has already been triggered.
         */

        if (
          reloadTriggered.current
        ) {

          return;
        }


        const currentDevicePixelRatio =
          window.devicePixelRatio;

        const previousDevicePixelRatio =
          initialDevicePixelRatio.current;


        /*
         * Browser zoom changed.
         */

        if (
          currentDevicePixelRatio !==
          previousDevicePixelRatio
        ) {

          reloadTriggered.current =
            true;


          console.log(
            '====================================================',
          );

          console.log(
            'BROWSER ZOOM CHANGED',
          );

          console.log(
            'Previous devicePixelRatio:',
            previousDevicePixelRatio,
          );

          console.log(
            'Current devicePixelRatio:',
            currentDevicePixelRatio,
          );

          console.log(
            'Refreshing page automatically...',
          );

          console.log(
            '====================================================',
          );


          /*
           * Small delay allows Chrome to finish applying the new zoom
           * level before reloading the application.
           */

          window.setTimeout(
            () => {

              window.location.reload();

            },
            100,
          );

        }

      };


    /* ========================================================================
     * POLLING
     *
     * This is the important part.
     *
     * Chrome does not reliably fire resize/matchMedia events for every
     * browser zoom change.
     *
     * Therefore we check devicePixelRatio every 200ms.
     *
     * This does NOT continuously refresh the page.
     *
     * The page reloads ONLY when devicePixelRatio changes.
     * ====================================================================== */

    const zoomCheckInterval =
      window.setInterval(
        checkBrowserZoom,
        200,
      );


    /* ========================================================================
     * ALSO LISTEN TO RESIZE
     *
     * This makes detection faster when Chrome does fire resize.
     * ====================================================================== */

    const handleResize =
      () => {

        checkBrowserZoom();

      };


    window.addEventListener(
      'resize',
      handleResize,
      {
        passive: true,
      },
    );


    /* ========================================================================
     * VISUAL VIEWPORT
     *
     * Some Chromium versions expose viewport changes through visualViewport.
     * ====================================================================== */

    const visualViewport =
      window.visualViewport;


    if (
      visualViewport
    ) {

      visualViewport.addEventListener(
        'resize',
        handleResize,
      );
    }


    /* ========================================================================
     * CLEANUP
     * ====================================================================== */

    return () => {

      window.clearInterval(
        zoomCheckInterval,
      );


      window.removeEventListener(
        'resize',
        handleResize,
      );


      if (
        visualViewport
      ) {

        visualViewport.removeEventListener(
          'resize',
          handleResize,
        );
      }

    };

  }, []);
};


/* ============================================================================
 * HELPER
 * ========================================================================== */

/**
 * Converts API time values into decimal hours.
 *
 * Examples:
 *
 * "01:12:36" -> 1.21
 * "08:34:12" -> 8.57
 * "+00:00:00" -> 0
 * "-01:30:00" -> -1.50
 *
 * Also supports numeric values.
 */

const timeToHours = (
  value:
    | string
    | number
    | null
    | undefined,
): number => {

  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {

    return 0;
  }


  /* --------------------------------------------------------------------------
     Already a number
     -------------------------------------------------------------------------- */

  if (
    typeof value === 'number'
  ) {

    return Number.isFinite(
      value,
    )
      ? value
      : 0;
  }


  let valueString =
    String(
      value,
    ).trim();


  if (
    !valueString
  ) {

    return 0;
  }


  /* --------------------------------------------------------------------------
     Handle positive / negative sign
     -------------------------------------------------------------------------- */

  let sign = 1;


  if (
    valueString.startsWith(
      '+',
    )
  ) {

    valueString =
      valueString.substring(
        1,
      );

  } else if (
    valueString.startsWith(
      '-',
    )
  ) {

    sign = -1;

    valueString =
      valueString.substring(
        1,
      );
  }


  /* --------------------------------------------------------------------------
     HH:mm:ss / HH:mm
     -------------------------------------------------------------------------- */

  if (
    valueString.includes(':')
  ) {

    const parts =
      valueString
        .split(':')
        .map(Number);


    /* HH:mm:ss */

    if (
      parts.length === 3
    ) {

      const [
        hours,
        minutes,
        seconds,
      ] = parts;


      if (
        Number.isFinite(
          hours,
        ) &&
        Number.isFinite(
          minutes,
        ) &&
        Number.isFinite(
          seconds,
        )
      ) {

        return (
          sign *
          (
            hours +
            minutes / 60 +
            seconds / 3600
          )
        );
      }


      return 0;
    }


    /* HH:mm */

    if (
      parts.length === 2
    ) {

      const [
        hours,
        minutes,
      ] = parts;


      if (
        Number.isFinite(
          hours,
        ) &&
        Number.isFinite(
          minutes,
        )
      ) {

        return (
          sign *
          (
            hours +
            minutes / 60
          )
        );
      }


      return 0;
    }


    return 0;
  }


  /* --------------------------------------------------------------------------
     Numeric string
     -------------------------------------------------------------------------- */

  const numericValue =
    Number(
      valueString,
    );


  return Number.isFinite(
    numericValue,
  )
    ? numericValue
    : 0;
};


/* ============================================================================
 * LOADER
 * ========================================================================== */

const ActiveHoursLoader = () => {

  return (
    <div
      className="
        flex
        min-h-[560px]
        w-full
        flex-col
        items-center
        justify-center
        rounded-[12px]
        bg-[#F8F9FB]
      "
    >

      {/* ====================================================================
          SPINNER
      ==================================================================== */}

      <div
        className="
          h-[40px]
          w-[40px]
          animate-spin
          rounded-full
          border-[4px]
          border-[#E4E7EC]
          border-t-[#2563EB]
        "
      />


      {/* ====================================================================
          MAIN LOADING TEXT
      ==================================================================== */}

      <p
        className="
          mt-[12px]
          text-[13px]
          font-medium
          text-[#475467]
        "
      >
        Loading Active Hours...
      </p>


      {/* ====================================================================
          SUB TEXT
      ==================================================================== */}

      <p
        className="
          mt-[4px]
          text-[11px]
          text-[#98A2B3]
        "
      >
        Fetching your activity data
      </p>

    </div>
  );
};


/* ============================================================================
 * PAGE
 * ========================================================================== */

const EmployeeActiveHoursReport = () => {

  const {
    hideLoader,
  } = useLoaderContext();


  /* ==========================================================================
     BROWSER ZOOM AUTO REFRESH

     This runs while Employee Active Hours page is mounted.

     Example:

     80%
       ↓
     100%

     Automatically reloads.

     100%
       ↓
     125%

     Automatically reloads.

     125%
       ↓
     100%

     Automatically reloads.
     ========================================================================== */

  useBrowserZoomRefresh();


  /* ==========================================================================
     API DATA
     ========================================================================== */

  const {
    loading,
    error,
    kpis,
    hourly,
    hourlyTarget,
    utilisation,
    wtdTrend,
    meVsTeam,
    history30Days,
    weeklyDetail,
  } =
    useEmployeeActiveHours();


  /* ==========================================================================
     CONVERT KPI TIME VALUES
     ========================================================================== */

  const activeTodayHours =
    timeToHours(
      kpis.activeToday,
    );


  const sevenDayAverageHours =
    timeToHours(
      kpis.sevenDayAvg,
    );


  const utilization =
    Number(
      kpis.utilization ?? 0,
    );


  /* ==========================================================================
     GLOBAL LOADER

     This follows the same behavior as your other reports.

     When the hook starts loading:
       loading = true

     When all APIs finish:
       loading = false

     Then the global loader is hidden.
     ========================================================================== */

  useEffect(() => {

    if (
      !loading
    ) {

      const timer =
        window.setTimeout(
          () => {

            hideLoader();

          },
          300,
        );


      return () => {

        window.clearTimeout(
          timer,
        );

      };

    }


    return undefined;

  }, [
    loading,
    hideLoader,
  ]);


  /* ==========================================================================
     ERROR
     ========================================================================== */

  if (
    error
  ) {

    return (

      <div
        className="
          min-h-screen
          w-full
          overflow-x-hidden
          bg-[#F4F5F7]

          px-2
          pb-2

          sm:px-3
          sm:pb-3

          lg:px-4
          lg:pb-4
        "
      >

        {/* ==================================================================
            HEADER
        ================================================================== */}

        <DashboardHeader
          showBackButton
          breadcrumbs={[
            {
              label:
                'Work Vista',

              path:
                '/',
            },

            {
              label:
                'My Dashboard',

              path:
                '/employee-dashboard',
            },

            {
              label:
                'Active Hours',
            },
          ]}
        />


        {/* ==================================================================
            ERROR CARD
        ================================================================== */}

        <div
          className="
            -mt-[14px]

            rounded-[16px]

            border
            border-[#D0D5DD]

            bg-[#F8F9FB]

            p-3

            sm:p-4
          "
        >

          <div
            className="
              rounded-[12px]

              border
              border-red-200

              bg-white

              p-4

              text-sm
              text-red-500
            "
          >

            {error}

          </div>

        </div>

      </div>
    );
  }


  /* ==========================================================================
     PAGE
     ========================================================================== */

  return (

    <div
      className="
        min-h-screen
        w-full
        overflow-x-hidden
        bg-[#F4F5F7]

        px-2
        pb-2

        sm:px-3
        sm:pb-3

        lg:px-4
        lg:pb-4
      "
    >

      {/* ======================================================================
          DASHBOARD HEADER
      ====================================================================== */}

      <DashboardHeader
        showBackButton
        breadcrumbs={[
          {
            label:
              'Work Vista',

            path:
              '/',
          },

          {
            label:
              'My Dashboard',

            path:
              '/employee-dashboard',
          },

          {
            label:
              'Active Hours',
          },
        ]}
      />


      {/* ======================================================================
          MAIN CONTAINER
      ====================================================================== */}

      <main
        className="
          -mt-[14px]

          w-full
          min-w-0

          rounded-[16px]

          border
          border-[#D0D5DD]

          bg-[#F8F9FB]

          p-3

          sm:p-4
        "
      >

        {/* ====================================================================
            LOADING
        ==================================================================== */}

        {loading ? (

          <ActiveHoursLoader />

        ) : (

          <>

            {/* ================================================================
                ACTIVE HOURS HEADER
            ================================================================ */}

            <section
              className="
                w-full
                min-w-0
              "
            >

              <ActiveHoursHeader
                activeToday={
                  activeTodayHours
                }

                sevenDayAvg={
                  sevenDayAverageHours
                }

                utilisation={
                  utilization
                }
              />

            </section>


            {/* ================================================================
                KPI CARDS
            ================================================================ */}

            <section
              className="
                mt-4

                w-full
                min-w-0
              "
            >

              <ActiveHoursKpiCards
                data={
                  kpis
                }
              />

            </section>


            {/* ================================================================
                HOURLY ACTIVE MINUTES + TIME UTILISATION

                Small screens:
                  1 column

                Desktop / 100% zoom:
                  Hourly chart = 8 columns
                  Donut = 4 columns

                110% zoom:
                  Still side-by-side

                125% zoom:
                  Still side-by-side
            ================================================================ */}

            <section
              className="
                mt-4

                grid

                w-full
                min-w-0

                grid-cols-1

                gap-4

                lg:grid-cols-12

                lg:items-stretch
              "
            >

              {/* ==============================================================
                  HOURLY ACTIVE MINUTES
              ============================================================== */}

              <div
                className="
                  col-span-1

                  min-w-0
                  w-full

                  lg:col-span-8
                "
              >

                <div
                  className="
                    h-full
                    w-full
                    min-w-0
                  "
                >

                  <HourlyActiveMinutesChart
                    data={
                      hourly
                    }

                    target={
                      hourlyTarget
                    }
                  />

                </div>

              </div>


              {/* ==============================================================
                  TIME UTILISATION
              ============================================================== */}

              <div
                className="
                  col-span-1

                  min-w-0
                  w-full

                  lg:col-span-4
                "
              >

                <div
                  className="
                    h-full
                    w-full
                    min-w-0
                  "
                >

                  <TimeUtilisationDonut
                    data={
                      utilisation
                    }
                  />

                </div>

              </div>

            </section>


            {/* ================================================================
                WTD ACTIVE HOURS + ME VS TEAM

                Both charts remain side-by-side at lg and above.
            ================================================================ */}

            <section
              className="
                mt-4

                grid

                w-full
                min-w-0

                grid-cols-1

                gap-4

                lg:grid-cols-2

                lg:items-stretch
              "
            >

              {/* ==============================================================
                  WTD ACTIVE HOURS
              ============================================================== */}

              <div
                className="
                  min-w-0
                  w-full
                "
              >

                <WTDActiveHoursTrend
                  data={
                    wtdTrend
                  }
                />

              </div>


              {/* ==============================================================
                  ME VS TEAM
              ============================================================== */}

              <div
                className="
                  min-w-0
                  w-full
                "
              >

                <MeVsTeamActiveHours
                  data={
                    meVsTeam
                  }
                />

              </div>

            </section>


            {/* ================================================================
                30 DAYS HISTORY
            ================================================================ */}

            <section
              className="
                mt-4

                w-full
                min-w-0
              "
            >

              <ActiveHoursHistory30Days
                data={
                  history30Days
                }
              />

            </section>


            {/* ================================================================
                WEEKLY ACTIVE HOURS DETAIL
            ================================================================ */}

            <section
              className="
                mt-4

                w-full
                min-w-0
              "
            >

              <WeeklyActiveHoursDetail
                data={
                  weeklyDetail
                }
              />

            </section>

          </>

        )}

      </main>

    </div>
  );
};


export default EmployeeActiveHoursReport;